import React, { useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  PhotographIcon,
  UploadIcon,
  TrashIcon,
  PencilIcon,
  StarIcon,
  CheckCircleIcon,
  XCircleIcon,
  SwitchVerticalIcon,
} from "@heroicons/react/outline";
import { StarIcon as StarSolidIcon } from "@heroicons/react/solid";
import {
  uploadStoreImagesThunk,
  updateStoreImageThunk,
  deleteStoreImageThunk,
  reorderStoreImagesThunk,
  setPrimaryImageThunk,
} from "../../../../store/thunks/branchDisplayThunks";
import Dropdown from "../../../common/Dropdown";

const IMAGE_TYPES = [
  { _id: "STOREFRONT", name: "Storefront" },
  { _id: "INTERIOR", name: "Interior" },
  { _id: "SHOWCASE", name: "Showcase" },
  { _id: "PRODUCT_DISPLAY", name: "Product Display" },
  { _id: "AMBIANCE", name: "Ambiance" },
  { _id: "TEAM", name: "Team" },
  { _id: "EVENT", name: "Event" },
  { _id: "OTHER", name: "Other" },
];

const ImagesSection = ({ branchCode, isRootAdmin }) => {
  const dispatch = useDispatch();
  const {
    images = [],
    uploading,
    loadingSection,
  } = useSelector((state) => state.branchDisplay);
  const fileInputRef = useRef(null);

  const [selectedType, setSelectedType] = useState(IMAGE_TYPES[0]);
  const [editingImageId, setEditingImageId] = useState(null);
  const [editForm, setEditForm] = useState({
    caption: "",
    altText: "",
    type: "",
  });
  const [draggedIndex, setDraggedIndex] = useState(null);

  // Debug: Log images structure on mount/update
  React.useEffect(() => {
    if (images && images.length > 0) {
      console.log("Images structure:", images[0]);
      console.log("Available keys:", Object.keys(images[0]));
    }
  }, [images]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const formData = new FormData();
    files.forEach((file) => {
      formData.append("images", file);
    });
    formData.append("type", selectedType._id);

    await dispatch(uploadStoreImagesThunk({ branchCode, formData }));
    e.target.value = "";
  };

  const handleDelete = async (image) => {
    if (!image._id) {
      console.error("Image _id is missing:", image);
      return;
    }
    if (window.confirm("Are you sure you want to delete this image?")) {
      await dispatch(deleteStoreImageThunk({ branchCode, imageId: image._id }));
    }
  };

  const handleSetPrimary = async (image) => {
    if (!image._id) {
      console.error("Image _id is missing:", image);
      return;
    }
    await dispatch(setPrimaryImageThunk({ branchCode, imageId: image._id }));
  };

  const handleEditClick = (image) => {
    if (!image._id) {
      console.error("Image _id is missing:", image);
      return;
    }
    setEditingImageId(image._id);
    setEditForm({
      caption: image.caption || "",
      altText: image.altText || "",
      type: image.type || "",
    });
  };

  const handleSaveEdit = async () => {
    if (editingImageId) {
      await dispatch(
        updateStoreImageThunk({
          branchCode,
          imageId: editingImageId,
          imageData: editForm,
        })
      );
    }
    setEditingImageId(null);
    setEditForm({ caption: "", altText: "", type: "" });
  };

  // Sort by primary first, then by displayOrder
  const displayImages = [...images].sort((a, b) => {
    if (a.isPrimary && !b.isPrimary) return -1;
    if (!a.isPrimary && b.isPrimary) return 1;
    return (a.displayOrder || 0) - (b.displayOrder || 0);
  });

  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) return;

    // Work with the sorted displayImages array
    const reorderedImages = [...displayImages];
    const [draggedItem] = reorderedImages.splice(draggedIndex, 1);
    reorderedImages.splice(dropIndex, 0, draggedItem);

    // Build imageOrder array with { imageId, displayOrder }
    const imageOrder = reorderedImages.map((img, idx) => ({
      imageId: img._id,
      displayOrder: idx,
    }));

    await dispatch(reorderStoreImagesThunk({ branchCode, imageOrder }));
    setDraggedIndex(null);
  };

  return (
    <div className="space-y-6">
      {/* Upload Section */}
      {isRootAdmin && (
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-medium mb-4 flex items-center">
            <UploadIcon className="h-4 w-4 mr-2" />
            Upload Images
          </h4>

          <div className="flex flex-wrap items-end gap-4">
            <div className="w-48">
              <Dropdown
                label="Image Type"
                options={IMAGE_TYPES}
                selected={selectedType}
                onChange={setSelectedType}
                placeholder="Select type"
              />
            </div>

            <div>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept="image/*"
                multiple
                onChange={handleUpload}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 flex items-center gap-2"
              >
                {uploading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <UploadIcon className="h-4 w-4" />
                )}
                {uploading ? "Uploading..." : "Select Images"}
              </button>
            </div>

            <p className="text-sm text-gray-500">
              Max 10 images at once. Supported formats: JPG, PNG, WebP
            </p>
          </div>
        </div>
      )}

      {/* Images Grid */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <div className="flex justify-between items-center mb-4">
          <h4 className="font-medium flex items-center">
            <PhotographIcon className="h-4 w-4 mr-2" />
            Store Images ({images.length})
          </h4>
          {isRootAdmin && images.length > 1 && (
            <p className="text-sm text-gray-500 flex items-center gap-1">
              <SwitchVerticalIcon className="h-4 w-4" />
              Drag to reorder
            </p>
          )}
        </div>

        {images.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <PhotographIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
            <p>No images uploaded yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {displayImages.map((image, index) => (
              <div
                key={image._id || image.id || `img-${index}`}
                draggable={isRootAdmin}
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDrop={(e) => handleDrop(e, index)}
                className={`relative group rounded-lg overflow-hidden border ${
                  draggedIndex === index ? "opacity-50" : ""
                } ${image.isPrimary ? "ring-2 ring-yellow-500" : ""}`}
              >
                <img
                  src={image.thumbnail || image.url}
                  alt={image.altText || image.caption || "Store image"}
                  className="w-full h-40 object-cover"
                />

                {/* Primary Badge */}
                {image.isPrimary && (
                  <div className="absolute top-2 left-2 px-2 py-1 bg-yellow-500 text-white text-xs rounded-full flex items-center gap-1">
                    <StarSolidIcon className="h-3 w-3" />
                    Primary
                  </div>
                )}

                {/* Type Badge */}
                <div className="absolute top-2 right-2 px-2 py-1 bg-black bg-opacity-50 text-white text-xs rounded">
                  {image.type || "OTHER"}
                </div>

                {/* Actions Overlay */}
                {isRootAdmin && (
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                    {!image.isPrimary && (
                      <button
                        onClick={() => handleSetPrimary(image)}
                        className="p-2 bg-yellow-500 text-white rounded-full hover:bg-yellow-600"
                        title="Set as primary"
                      >
                        <StarIcon className="h-4 w-4" />
                      </button>
                    )}
                    <button
                      onClick={() => handleEditClick(image)}
                      className="p-2 bg-blue-500 text-white rounded-full hover:bg-blue-600"
                      title="Edit"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(image)}
                      className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600"
                      title="Delete"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                )}

                {/* Caption */}
                {image.caption && (
                  <div className="absolute bottom-0 left-0 right-0 px-2 py-1 bg-black bg-opacity-50 text-white text-xs truncate">
                    {image.caption}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editingImageId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h4 className="font-medium mb-4">Edit Image</h4>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Caption
                </label>
                <input
                  type="text"
                  value={editForm.caption}
                  onChange={(e) =>
                    setEditForm((prev) => ({
                      ...prev,
                      caption: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Image caption"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Alt Text
                </label>
                <input
                  type="text"
                  value={editForm.altText}
                  onChange={(e) =>
                    setEditForm((prev) => ({
                      ...prev,
                      altText: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Alt text for accessibility"
                />
              </div>

              <div>
                <Dropdown
                  label="Image Type"
                  options={IMAGE_TYPES}
                  selected={IMAGE_TYPES.find((t) => t._id === editForm.type)}
                  onChange={(selected) =>
                    setEditForm((prev) => ({
                      ...prev,
                      type: selected?._id || "",
                    }))
                  }
                  placeholder="Select type"
                />
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <button
                onClick={handleSaveEdit}
                disabled={loadingSection === "images"}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 flex items-center gap-2"
              >
                <CheckCircleIcon className="h-4 w-4" />
                Save
              </button>
              <button
                onClick={() => {
                  setEditingImageId(null);
                  setEditForm({ caption: "", altText: "", type: "" });
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImagesSection;
